// src/components/PartnerLockup.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The partner lockup: ICENECDEV and Ecotour Enterprise, shown together as
// equals. This replaces the old single-logo OrgLockup, which said "deployed
// under ICENECDEV" and sat at text scale beside the line it qualified.
//
// EVERY use goes through this file, so the wording, the ordering and — most
// importantly — the relative sizing of the two marks stay identical wherever
// they appear. Call sites never reach for the PNGs directly.
//
//
// WHY THE TWO LOGOS ARE NOT THE SAME PIXEL HEIGHT
// ────────────────────────────────────────────────
// They are different *kinds* of lockup, so equal pixel height would not read as
// equal size:
//
//   ICENECDEV  a stacked mark — a large leaf-in-circle sitting above a small
//              wordmark. Trimmed content 390×314, aspect 1.242. Its wordmark
//              is 51px of that 314, i.e. 16.2% of the logo's height.
//   Ecotour    a horizontal mark — a small circular badge beside two lines of
//              type. Trimmed content 200×72, aspect 2.778. Its type is ~22px
//              of that 72, i.e. 30.6% of the logo's height.
//
// Set both to the same height and ICENECDEV dominates: it carries roughly 2.2×
// the area, and its mark towers over Ecotour's badge. Two standard ways to
// correct that disagree, so this takes the middle of them:
//
//   equal cap-height  (match the two wordmarks)   Ecotour = 0.53 × ICENECDEV
//   equal area        (match the two footprints)  Ecotour = 0.669 × ICENECDEV
//   ── used here ─────────────────────────────────────────────── 0.62 ────────
//
// At 0.62 the pair measures 75×60 against 103×37: comparable widths, close
// footprints (4500 vs 3811 px²), and Ecotour's type ends up ~13% larger than
// ICENECDEV's, which is what stops the shorter logo from reading as junior.
// That is the "optical size, not pixel height" the brief asked for.
//
//
// WHY THE SIZES ARE CAPPED
// ────────────────────────
// The Ecotour master supplied is 200×72 of usable content, and that is the
// whole budget. Every size below is chosen so its rendered height stays at or
// under ~half the source height, which keeps it at 2× — crisp on a high-DPI
// screen. The band tops out at 40px of Ecotour (1.8×) and goes no further; it
// does not grow on a large monitor, because past that it starts to soften. See
// NOTES.md — a vector or a 2× raster from Ecotour would lift this cap.
// ─────────────────────────────────────────────────────────────────────────────

import { C, F, TYPE, BRAND } from "../constants";
import { PARTNERS } from "../eh/org";
import icenecdevLogo from "../assets/partners/icenecdev.png";
import ecotourLogo from "../assets/partners/ecotour.png";

// Natural (trimmed) pixel dimensions and the optical scale derived above.
// `w`/`h` are the real content sizes, so width is always computed rather than
// guessed and the aspect ratio can never be squashed by a stray style.
const LOGOS = {
  icenecdev: { src: icenecdevLogo, w: 390, h: 314, scale: 1 },
  ecotour: { src: ecotourLogo, w: 200, h: 72, scale: 0.62 },
};

// Trilingual, and kept here rather than in eh/i18n.js because the partner
// wording is chrome shared by the landing page, the app shell and four
// footers — none of which is the environmental-health module. One dictionary,
// one place to edit.
const PARTNER_T = {
  en: { with: "In partnership with", under: "Deployed under" },
  fr: { with: "En partenariat avec", under: "Déployé sous" },
  pid: { with: "Wi dey work with", under: "Dem deploy am under" },
};

const tr = (lang) => PARTNER_T[lang] || PARTNER_T.en;

// The band's ICENECDEV height. Fluid, then held — and the cap is arithmetic,
// not taste. Ecotour's source is 72px tall, so on a 2× screen it can fill at
// most 72 device pixels, i.e. 36 CSS px. 36 ÷ 0.62 = 58, so ICENECDEV's height
// caps at 58 and Ecotour lands on exactly its own resolution. Raising this
// number upscales Ecotour and softens it; it may only move when a better
// Ecotour master replaces the one in src/assets/partners.
const BAND_BASE = { min: 46, vw: 5.4, max: 58 };

// Both plates are the same height by construction rather than by whatever
// their contents happen to measure. Sizing them from content made the
// ICENECDEV plate 8px taller than the Ecotour plate at desktop — the taller
// mark pushing its own box — which is exactly the asymmetry the equal-fraction
// columns exist to prevent. Tallest case is a 58px mark plus 20px of padding
// top and bottom.
const TILE_H = "clamp(88px, 9.5vw, 98px)";

/**
 * Scale a height that may be a number (px) or a clamp spec, keeping the
 * relationship between the two logos exact at every viewport width.
 *
 * A clamp spec `{ min, vw, max }` becomes `clamp(Apx, Bvw, Cpx)` with all
 * three terms multiplied, so the 0.62 ratio holds while the pair is still
 * fluid — no media query and no resize listener needed for the sizing.
 */
function scaleHeight(height, k) {
  if (typeof height === "number") return `${+(height * k).toFixed(2)}px`;
  const { min, vw, max } = height;
  return `clamp(${+(min * k).toFixed(2)}px, ${+(vw * k).toFixed(3)}vw, ${+(max * k).toFixed(2)}px)`;
}

/**
 * One partner mark.
 *
 * @param {string} id      key into LOGOS / an entry in PARTNERS
 * @param {number|{min,vw,max}} base  the height ICENECDEV renders at; the other
 *                                    logo is scaled off it, never sized alone
 * @param {boolean} labelled  true when a text name sits next to the mark, in
 *                            which case the image is decorative to a screen
 *                            reader instead of repeating that name
 */
export function PartnerLogo({ id, base, labelled = false }) {
  const logo = LOGOS[id];
  const meta = PARTNERS.find((p) => p.id === id);
  if (!logo || !meta) return null;

  const h = scaleHeight(base, logo.scale);

  return (
    <img
      src={logo.src}
      // Intrinsic size, so the browser reserves the right box before the image
      // arrives and the band does not jump as it loads.
      width={logo.w}
      height={logo.h}
      alt={labelled ? "" : meta.name}
      aria-hidden={labelled ? "true" : undefined}
      style={{
        height: h,
        // `auto` is what preserves the aspect ratio; the explicit ratio is a
        // belt-and-braces guard so a flex parent can never stretch the mark.
        width: "auto",
        aspectRatio: `${logo.w} / ${logo.h}`,
        display: "block",
        flexShrink: 0,
        // The marks are supplied on white. On the app's off-white surfaces that
        // edge would show, so anything holding a logo paints white behind it —
        // see `tileStyle` and the strip below.
        objectFit: "contain",
      }}
    />
  );
}

/** The white plate a mark sits on, which is also what gives it its clear space. */
const tileStyle = (pad) => ({
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  padding: pad,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: C.shadowSm,
});

/**
 * The dedicated partner band — the landing page's full statement of the
 * partnership.
 *
 * The two partners are equal-width grid columns holding identically-styled
 * tiles. That is the part doing the work: whatever the marks inside are doing,
 * the structure around them is symmetrical, so neither can read as the senior
 * of the pair.
 *
 * @param {string} lang   en | fr | pid
 * @param {string} label  overrides the eyebrow ("In partnership with")
 */
export function PartnerBand({ lang = "en", label, style = {} }) {
  const t = tr(lang);

  return (
    <div style={{ fontFamily: F, ...style }}>
      {/* Decorative accent rule, and the only place the logo colours are used
          as colour. Split in two — ICENECDEV's green, then Ecotour's navy into
          its gold — so the rule itself says "two organisations". It keys
          nothing and means nothing, which is why it is allowed to be green and
          gold in an app where those hues belong to the risk scale. */}
      <div
        style={{
          height: 3,
          borderRadius: "3px 3px 0 0",
          background: `linear-gradient(90deg, ${BRAND.icenecdev} 0%, ${BRAND.icenecdev} 50%, ${BRAND.ecotourNavy} 50%, ${BRAND.ecotourNavy} 82%, ${BRAND.ecotourGold} 100%)`,
        }}
      />

      <div
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderTop: "none",
          borderRadius: "0 0 16px 16px",
          boxShadow: C.shadowMd,
          padding: "clamp(20px, 3.5vw, 30px) clamp(14px, 3vw, 30px)",
        }}
      >
        <div
          style={{
            ...TYPE.micro,
            color: C.dim,
            textAlign: "center",
            marginBottom: "clamp(16px, 2.6vw, 22px)",
          }}
        >
          {label || t.with}
        </div>

        {/* Capped and centred. Left to fill a 1080px page the two columns
            become ~490px plates holding a 79px mark, which reads as two logos
            adrift in a lot of white rather than as a lockup. */}
        <div className="cc-partner-grid" style={{ maxWidth: 600, margin: "0 auto" }}>
          {PARTNERS.map((p) => (
            <PartnerTile key={p.id} partner={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** One column of the band: tile, then the organisation's name in text. */
function PartnerTile({ partner }) {
  const inner = (
    <>
      <div
        style={{
          ...tileStyle("0 clamp(16px, 3vw, 26px)"),
          height: TILE_H,
        }}
      >
        <PartnerLogo id={partner.id} base={BAND_BASE} labelled />
      </div>
      <div
        style={{
          marginTop: 12,
          fontSize: 12.5,
          fontWeight: 800,
          color: C.text,
          textAlign: "center",
          letterSpacing: "-0.1px",
        }}
      >
        {partner.short}
      </div>
      {partner.name !== partner.short && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: C.muted,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {partner.name}
        </div>
      )}
    </>
  );

  // ICENECDEV links out; Ecotour has no verified URL on record, so it is plain
  // text. The two columns still look identical — only the interaction differs.
  return partner.href ? (
    <a
      href={partner.href}
      target="_blank"
      rel="noreferrer noopener"
      style={{ textDecoration: "none", display: "block", fontFamily: F }}
    >
      {inner}
    </a>
  ) : (
    <div style={{ fontFamily: F }}>{inner}</div>
  );
}

/**
 * The compact pair — a slim horizontal strip of both marks with a divider
 * between them. Used in the app shell under the top bar, in the desktop side
 * rail, and in the screen footers.
 *
 * No text names here: the strip is chrome that repeats on every screen, and the
 * readable, named version of the partnership lives in the footers and on the
 * landing band.
 *
 * @param {number|{min,vw,max}} base   ICENECDEV's height; Ecotour follows at 0.62
 * @param {boolean} showLabel  render the "In partnership with" eyebrow
 * @param {boolean} tiles      put each mark on its own white plate (for use on
 *                             a tinted surface); off means bare marks
 */
export function PartnerStrip({
  lang = "en",
  base = 30,
  showLabel = true,
  label,
  labelClassName,
  tiles = false,
  align = "center",
  style = {},
}) {
  const t = tr(lang);
  const marks = PARTNERS.map((p) => {
    const mark = <PartnerLogo key={p.id} id={p.id} base={base} />;
    // alignSelf:stretch, not a computed height. Content-sized plates came out
    // at two different heights — the shorter mark making the shorter box —
    // which is the same asymmetry the band's fixed tile height exists to
    // prevent. Stretching both to the flex line (set by the taller mark) makes
    // them equal without the call site having to know either logo's aspect.
    return tiles ? (
      <div key={p.id} style={{ ...tileStyle("7px 10px"), alignSelf: "stretch" }}>
        {mark}
      </div>
    ) : (
      mark
    );
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: align,
        flexWrap: "wrap",
        gap: showLabel ? 12 : 0,
        fontFamily: F,
        ...style,
      }}
    >
      {showLabel && (
        <span
          className={labelClassName}
          style={{ ...TYPE.micro, fontSize: 9.5, color: C.dim, letterSpacing: "0.6px" }}
        >
          {label || t.with}
        </span>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {marks[0]}
        {/* Optical divider. Its height tracks the taller mark rather than
            being a fixed number, so the gap reads the same at every base. */}
        <span
          aria-hidden="true"
          style={{
            width: 1,
            alignSelf: "stretch",
            minHeight: typeof base === "number" ? Math.round(base * 0.72) : 24,
            background: C.border,
            flexShrink: 0,
          }}
        />
        {marks[1]}
      </div>
    </div>
  );
}

export default PartnerBand;
